import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Paper,
  IconButton
} from '@mui/material';
import { useOnboarding } from '../contexts/OnboardingContext';
import { X } from 'react-feather';

const onboardingSteps = [
  {
    title: '환영합니다!',
    description: 'OnTimeLog에 오신 것을 환영합니다.',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          이 튜토리얼을 통해 시스템의 주요 기능을 빠르게 익힐 수 있습니다.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          약 3분 정도 소요됩니다.
        </Typography>
      </Box>
    )
  },
  {
    title: '투입시간 입력',
    description: '업무 투입시간을 쉽게 입력하세요',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          <strong>업무 투입시간</strong> 메뉴에서:
        </Typography>
        <ul>
          <li>프로젝트와 계약을 선택합니다</li>
          <li>업무 내용과 투입 시간을 입력합니다</li>
          <li>스프레드시트 형식으로 빠르게 입력할 수 있습니다</li>
        </ul>
      </Box>
    )
  },
  {
    title: 'To-Do 리스트',
    description: '할 일을 관리하고 투입시간으로 변환하세요',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          <strong>To-Do 리스트</strong> 기능:
        </Typography>
        <ul>
          <li>자주 하는 업무를 To-Do로 등록합니다</li>
          <li>등록된 To-Do를 선택하여 투입시간 입력화면에 추가합니다</li>
          <li>빠른 입력이 가능합니다</li>
        </ul>
      </Box>
    )
  },
  {
    title: '대시보드 (Manager 이상)',
    description: '프로젝트별 투입시간을 한눈에 확인하세요',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          <strong>대시보드</strong>에서 확인할 수 있는 정보:
        </Typography>
        <ul>
          <li>고객사별 투입시간 통계</li>
          <li>프로젝트별 투입시간 통계</li>
          <li>실무자별 투입시간 및 입력 지연 비중</li>
        </ul>
      </Box>
    )
  },
  {
    title: '완료!',
    description: '이제 시스템을 사용할 준비가 되었습니다',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          언제든지 설정에서 이 튜토리얼을 다시 볼 수 있습니다.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          즐거운 사용 되세요!
        </Typography>
      </Box>
    )
  }
];

export default function OnboardingTutorial() {
  const {
    showOnboarding,
    currentStep,
    nextStep,
    prevStep,
    completeOnboarding,
    skipOnboarding
  } = useOnboarding();

  const isLastStep = currentStep === onboardingSteps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      completeOnboarding();
    } else {
      nextStep();
    }
  };

  const handleSkip = () => {
    skipOnboarding();
  };

  if (!showOnboarding) return null;

  const currentStepData = onboardingSteps[currentStep];

  return (
    <Dialog
      open={showOnboarding}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 2
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={handleSkip}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1
          }}
        >
          <X size={20} />
        </IconButton>

        <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
          {onboardingSteps.map((step, index) => (
            <Step key={index}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <DialogContent>
          <Typography variant="h5" gutterBottom>
            {currentStepData.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {currentStepData.description}
          </Typography>
          <Paper sx={{ p: 3, mt: 2, bgcolor: 'background.default' }}>
            {currentStepData.content}
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleSkip} color="inherit">
            건너뛰기
          </Button>
          <Box sx={{ flex: 1 }} />
          {currentStep > 0 && (
            <Button onClick={prevStep} sx={{ mr: 1 }}>
              이전
            </Button>
          )}
          <Button
            onClick={handleNext}
            variant="contained"
            color="primary"
          >
            {isLastStep ? '완료' : '다음'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

